import { useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FINISH_EVO } from "../../../../http/lock_calculator/types";
import { applyCascade } from "../features/notices/applyCascade";
import type { ReportCascade } from "../features/notices/NoticesContext";
import type { LockCalculatorFormValues } from "../model/form";
import { hasRealLoadValue } from "../model/structuralVisibility";

/**
 * Keeps UI-only dependent-field normalization in one place. The backend stays
 * authoritative; these effects only prevent impossible transient selections.
 * Every change a rule makes is reported as a notice ("x changed because of y").
 */
export const useLockCalculatorFormRules = (
  form: UseFormReturn<LockCalculatorFormValues>,
  values: Partial<LockCalculatorFormValues> | undefined,
  report: ReportCascade,
) => {
  const connectorCountInitialized = useRef(false);
  const orientationInitialized = useRef(false);

  useEffect(() => {
    if (!connectorCountInitialized.current) {
      connectorCountInitialized.current = true;
      return;
    }
    if (values?.connectorPairCount === 1) {
      applyCascade(form, report, {
        code: "secondConnectorCleared",
        causes: ["connectorPairCount"],
        effect: "connectorPairs.1.lockItemId",
        next: null,
      });
      form.setValue("connectorPairs.1.finish", FINISH_EVO);
    }
  }, [form, report, values?.connectorPairCount]);

  useEffect(() => {
    if (!orientationInitialized.current) {
      orientationInitialized.current = true;
      return;
    }
    const orientation = values?.headerMember?.orientation;
    const config = values?.lateralResistanceConfig;
    if (!orientation || config == null) return;
    const corrected = orientation === "column" && config === 1 ? 0 : orientation === "beam" && config === 0 ? 1 : null;
    if (corrected != null) {
      applyCascade(form, report, {
        code: "lateralConfigCorrected",
        causes: ["headerMember.orientation"],
        effect: "lateralResistanceConfig",
        next: corrected,
        params: { fromConfig: config, toConfig: corrected, orientation },
      });
    }
  }, [form, report, values?.headerMember?.orientation]);

  useEffect(() => {
    if (values?.lateralResistanceConfig !== 4) {
      applyCascade(form, report, {
        code: "lockstopCleared",
        causes: ["lateralResistanceConfig"],
        effect: "lockstopItemId",
        next: null,
      });
    }
  }, [form, report, values?.lateralResistanceConfig]);

  // A wider header lock and a lock stop exclude each other, and the result has to be recalculated either way.
  // Choosing the lock after the lock stop removes the lock stop and the lateral resistance type, which has to
  // be chosen again (reported once, by the lock stop).
  useEffect(() => {
    if (!values?.bom?.headerLockItemId) return;
    const hadLockstop = form.getValues("lockstopItemId") != null;
    applyCascade(form, report, {
      code: "lockstopClearedByHeaderLock",
      causes: ["bom.headerLockItemId"],
      effect: "lockstopItemId",
      next: null,
    });
    if (hadLockstop) {
      applyCascade(form, report, {
        code: "lockstopClearedByHeaderLock",
        causes: ["bom.headerLockItemId"],
        effect: "lateralResistanceConfig",
        next: null,
        silent: true,
      });
    }
  }, [form, report, values?.bom?.headerLockItemId]);

  // The other way round: choosing a lock stop after the wider lock removes the lock. It reads the form, not the
  // render, so a saved calculation holding both keeps what the rule above decided.
  useEffect(() => {
    if (!values?.lockstopItemId || !form.getValues("lockstopItemId")) return;
    applyCascade(form, report, {
      code: "headerLockClearedByLockstop",
      causes: ["lockstopItemId"],
      effect: "bom.headerLockItemId",
      next: null,
    });
  }, [form, report, values?.lockstopItemId]);

  useEffect(() => {
    const axes = [
      ["vertical", "includeVertical"],
      ["uplift", "includeUplift"],
      ["axial", "includeAxial"],
      ["lateral", "includeLateral"],
    ] as const;
    axes.forEach(([axis, include]) => {
      if (!hasRealLoadValue(values?.loads?.[axis])) {
        applyCascade(form, report, {
          code: "combinedAxisDisabled",
          causes: [`loads.${axis}`],
          effect: `combinedVerification.${include}`,
          next: false,
          params: { axis },
        });
      }
    });
  }, [form, report, values?.loads?.vertical, values?.loads?.uplift, values?.loads?.axial, values?.loads?.lateral]);

  // Lateral and Uplift Resistance Configuration share the same physical
  // inclined fastener (fasteners.inclinedItemId) — choosing "inclined fasteners"
  // on Lateral implies the same choice on Uplift too. One-directional only:
  // picking "inclined fasteners" on Uplift does NOT set Lateral, and picking a
  // different lateral mechanism (e.g. lockstop) does not clear an
  // already-chosen Uplift selection — confirmed as the intended behavior.
  useEffect(() => {
    if (values?.lateralResistanceConfig === 3) {
      applyCascade(form, report, {
        code: "upliftConfigSetByLateral",
        causes: ["lateralResistanceConfig"],
        effect: "upliftResistanceConfig",
        next: 0,
      });
    }
  }, [form, report, values?.lateralResistanceConfig]);

  // An Uplift Force (Fup,d) implies the inclined-fastener uplift resistance
  // mechanism — same one-directional stance as the rule above. A load of
  // exactly 0 doesn't count (hasRealLoadValue) — otherwise this wrongly
  // enables the Inclined fastener field in ConnectorsStep (and the matching
  // backend candidate-generation/filter gate, useInclined in
  // configuration_generator_service.go) for an axis that has nothing to resist.
  useEffect(() => {
    if (hasRealLoadValue(values?.loads?.uplift)) {
      applyCascade(form, report, {
        code: "upliftConfigSetByLoad",
        causes: ["loads.uplift"],
        effect: "upliftResistanceConfig",
        next: 0,
      });
    }
  }, [form, report, values?.loads?.uplift]);

  useEffect(() => {
    if (values?.lateralResistanceConfig !== 3 && values?.upliftResistanceConfig !== 0) {
      applyCascade(form, report, {
        code: "inclinedFastenerCleared",
        causes: ["lateralResistanceConfig", "upliftResistanceConfig"],
        effect: "fasteners.inclinedItemId",
        next: null,
      });
    }
  }, [form, report, values?.lateralResistanceConfig, values?.upliftResistanceConfig]);
};
